import type { ColumnDataType, ColumnUpdater } from '@blocksuite/affine-model';
import { dispatchDatabaseFileCellIndexReviewCopy } from '@blocksuite/affine-shared/services';
import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import {
  assertDatabaseColumnShapeWithinLimits,
  DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
  isTemporalProfileViewType,
  type PropertyMetaConfig,
  TEMPORAL_PROFILE_ROLE_FIELDS,
  type TypeInstance,
} from '@blocksuite/data-view';
import { propertyPresets } from '@blocksuite/data-view/property-presets';
import { FILE_INDEX_MANAGED_BY } from '@streamlain/database-identity';

import { type DatabaseFileCellReviewCopyRow } from './data-source-contracts.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import { isSystemManagedPropertyData } from './data-source-values.js';
import { databasePropertyConverts } from './properties/index.js';
import {
  addProperty,
  copyCellsByProperty,
  getProperty,
  updateCells,
  updateProperty,
} from './utils/block-utils.js';

export function propertyAdd(
  this: DatabaseSourceRuntime,
  insertToPosition: InsertToPosition,
  ops?: {
    type?: string;
    name?: string;
  }
): string | undefined {
  if (this._model.props.schemaLocked) return;
  assertDatabaseColumnShapeWithinLimits({
    source: `Database ${this.databaseId}`,
    columnCount: this.columnCount$.value + 1,
  });
  this.captureStorageSync();
  const { type, name } = ops ?? {};
  const property = this.propertyMetaGet(
    type ?? propertyPresets.multiSelectPropertyConfig.type
  );
  if (!property) {
    return;
  }
  const nextProperty = property.create(this.newPropertyName(name));
  this.assertFileIndexRootSchemaWithinLimits({
    columns: [...this._model.props.columns, nextProperty],
  });
  const result = addProperty(this._model, insertToPosition, nextProperty);
  return result;
}

export function getNormalPropertyAndIndex(
  this: DatabaseSourceRuntime,
  propertyId: string
):
  | {
      column: ColumnDataType<Record<string, unknown>>;
      index: number;
    }
  | undefined {
  const index = this._model.props.columns$.value.findIndex(
    v => v.id === propertyId
  );
  if (index >= 0) {
    const column = this._model.props.columns$.value[index];
    if (!column) {
      return;
    }
    return {
      column,
      index,
    };
  }
  return;
}

export function getPropertyAndIndex(
  this: DatabaseSourceRuntime,
  propertyId: string
):
  | {
      column: ColumnDataType<Record<string, unknown>>;
      index: number;
    }
  | undefined {
  const result = this.getNormalPropertyAndIndex(propertyId);
  if (result) {
    return result;
  }
  if (this.isFixedProperty(propertyId)) {
    const meta = this.propertyMetaGet(propertyId);
    if (!meta) {
      return;
    }
    const defaultData = meta.config.fixed?.defaultData ?? {};
    return {
      column: {
        data: defaultData,
        id: propertyId,
        type: propertyId,
        name: meta.config.name,
      },
      index: -1,
    };
  }
  return undefined;
}

export function updateSourceProperty(
  this: DatabaseSourceRuntime,
  id: string,
  updater: ColumnUpdater
): string | undefined {
  const result = this.getPropertyAndIndex(id);
  if (!result) {
    return;
  }
  const { column: prevColumn, index } = result;
  this.storageDoc.transact(() => {
    if (index >= 0) {
      const result = updater(prevColumn);
      this._model.props.columns[index] = { ...prevColumn, ...result };
    } else {
      const result = updater(prevColumn);
      this._model.props.columns = [
        ...this._model.props.columns,
        { ...prevColumn, ...result },
      ];
    }
  });
  return id;
}

export function propertyDataGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): Record<string, unknown> {
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return {};
  }
  return result.column.data;
}

export function propertyDataSet(
  this: DatabaseSourceRuntime,
  propertyId: string,
  data: Record<string, unknown>
): void {
  if (this._model.props.schemaLocked || this.propertyReadonlyGet(propertyId)) {
    return;
  }
  this.assertFileIndexRootSchemaWithinLimits({
    columns: this._model.props.columns.map(column =>
      column.id === propertyId ? { ...column, data } : column
    ),
  });
  this._runCapture();
  this.updateProperty(propertyId, () => ({ data }));
}

export function propertyDataTypeGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): TypeInstance | undefined {
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return;
  }
  const { column } = result;
  const meta = this.propertyMetaGet(column.type);
  if (!meta) {
    return;
  }
  return meta.config?.jsonValue.type({
    data: column.data,
    dataSource: this.dataSource,
  });
}

export function propertyDelete(this: DatabaseSourceRuntime, id: string): void {
  if (!this.canMutatePropertySchema(id)) {
    return;
  }
  if (this.isRowSharded) {
    this.captureStorageSync();
    const index = this._model.props.columns.findIndex(v => v.id === id);
    if (index < 0) return;
    this.storageDoc.transact(() => {
      this._model.props.columns = this._model.props.columns.filter(
        (_, i) => i !== index
      );
    });
    this.deleteShardedColumnFromLocalDocs(id);
    return;
  }
  this.assertNoLocalRootRowMutation('Property deletion');
  this.captureStorageSync();
  const index = this._model.props.columns.findIndex(v => v.id === id);
  if (index < 0) return;

  this.storageDoc.transact(() => {
    this._model.props.columns = this._model.props.columns.filter(
      (_, i) => i !== index
    );
  });
}

export function collectDatabaseFileCellReviewCopyRows(
  this: DatabaseSourceRuntime,
  propertyId: string
): DatabaseFileCellReviewCopyRow[] {
  const rows: DatabaseFileCellReviewCopyRow[] = [];
  for (const rowId of this.getLogicalRowIdsSnapshot()) {
    const value = this.cellValueGet(rowId, propertyId);
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }
    for (const [refId, rawItem] of Object.entries(
      value as Record<string, unknown>
    )) {
      if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) {
        continue;
      }
      const blobId =
        typeof (rawItem as { id?: unknown }).id === 'string'
          ? (rawItem as { id: string }).id
          : refId;
      if (!blobId) continue;
      rows.push({ rowId, refId, blobId });
    }
  }
  return rows;
}

export function dispatchDatabaseFileCellReviewCopy(
  this: DatabaseSourceRuntime,
  sourcePropertyId: string,
  targetPropertyId: string,
  rows: DatabaseFileCellReviewCopyRow[]
): void {
  if (rows.length === 0) return;
  dispatchDatabaseFileCellIndexReviewCopy({
    workspaceId: this.workspaceId,
    docId: this.docId,
    databaseId: this.databaseId,
    sourcePropertyId,
    targetPropertyId,
    rows,
  });
}

export function propertyDuplicate(
  this: DatabaseSourceRuntime,
  propertyId: string
): string | undefined {
  if (
    this.isFixedProperty(propertyId) ||
    this._model.props.schemaLocked ||
    this.propertyReadonlyGet(propertyId)
  ) {
    return;
  }
  assertDatabaseColumnShapeWithinLimits({
    source: `Database ${this.databaseId}`,
    columnCount: this.columnCount$.value + 1,
  });
  const currentSchema = getProperty(this._model, propertyId);
  if (!currentSchema) {
    return;
  }
  const fileCellReviewRows =
    currentSchema.type === 'attachment'
      ? this.collectDatabaseFileCellReviewCopyRows(propertyId)
      : [];
  this.captureStorageSync();
  const { id: copyId, ...nonIdProps } = currentSchema;
  const names = new Set(this._model.props.columns$.value.map(v => v.name));
  let index = 1;
  while (names.has(`${nonIdProps.name}(${index})`)) {
    index++;
  }
  const schema = {
    ...nonIdProps,
    id: this._model.store.workspace.idGenerator(),
    name: `${nonIdProps.name}(${index})`,
  };
  this.assertFileIndexRootSchemaWithinLimits({
    columns: [...this._model.props.columns, schema],
  });
  const id = addProperty(
    this._model,
    {
      before: false,
      id: propertyId,
    },
    schema
  );
  if (this.isRowSharded) {
    this.copyShardedCellsByProperty(copyId, id);
    this.dispatchDatabaseFileCellReviewCopy(copyId, id, fileCellReviewRows);
    return id;
  }
  this.assertNoLocalRootRowMutation('Property duplication');
  copyCellsByProperty(this._model, copyId, id);
  this.dispatchDatabaseFileCellReviewCopy(copyId, id, fileCellReviewRows);
  return id;
}

export function canMutatePropertySchema(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return (
    !this._model.props.schemaLocked &&
    !this.isFixedProperty(propertyId) &&
    !this.propertyReadonlyGet(propertyId) &&
    !this.isTemporalProfileRoleProperty(propertyId)
  );
}

export function isTemporalProfileRoleProperty(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return this._model.props.views.some(view => {
    if (!isTemporalProfileViewType(view.mode)) return false;
    const viewData = view as unknown as Record<string, unknown>;
    return Object.values(TEMPORAL_PROFILE_ROLE_FIELDS).some(fields =>
      fields.some(
        field =>
          this.readTemporalProfileViewPath(viewData, field) === propertyId
      )
    );
  });
}

export function readTemporalProfileViewPath(
  this: DatabaseSourceRuntime,
  viewData: Record<string, unknown>,
  path: string
): unknown {
  return path.split('.').reduce<unknown>((value, key) => {
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, viewData);
}

export function propertyCanDelete(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return this.canMutatePropertySchema(propertyId);
}

export function propertyCanDuplicate(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return (
    this.canMutatePropertySchema(propertyId) &&
    this.columnCount$.value < DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
  );
}

export function propertyTypeCanSet(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return this.canMutatePropertySchema(propertyId);
}

export function propertyMetaGet(
  this: DatabaseSourceRuntime,
  type: string
): PropertyMetaConfig | undefined {
  return this.propertyRegistry.value[type];
}

export function propertyNameGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): string {
  if (propertyId === 'type') {
    return 'Block Type';
  }
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return '';
  }
  return result.column.name;
}

export function propertyNameSet(
  this: DatabaseSourceRuntime,
  propertyId: string,
  name: string
): void {
  if (this._model.props.schemaLocked || this.propertyReadonlyGet(propertyId)) {
    return;
  }
  this.assertFileIndexRootSchemaWithinLimits({
    columns: this._model.props.columns.map(column =>
      column.id === propertyId ? { ...column, name } : column
    ),
  });
  this.captureStorageSync();
  this.updateProperty(propertyId, () => ({ name }));
}

export function propertyReadonlyGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  if (
    this._model.props.managedBy === FILE_INDEX_MANAGED_BY &&
    propertyId === 'title'
  ) {
    return true;
  }
  const readonlyProperties = new Set([
    'type',
    'created-time',
    'created-by',
    'updated-time',
    'updated-by',
  ]);
  if (readonlyProperties.has(propertyId)) {
    return true;
  }
  const propertyType = this.propertyTypeGet(propertyId);
  if (propertyType && readonlyProperties.has(propertyType)) {
    return true;
  }
  return isSystemManagedPropertyData(this.propertyDataGet(propertyId));
}

export function propertyTypeGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): string | undefined {
  if (propertyId === 'type') {
    return 'image';
  }
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return;
  }
  return result.column.type;
}

export function propertyTypeSet(
  this: DatabaseSourceRuntime,
  propertyId: string,
  toType: string
): void {
  if (!this.canMutatePropertySchema(propertyId)) {
    return;
  }
  if (this.isRowSharded) {
    this.propertyTypeSetSharded(propertyId, toType);
    return;
  }
  this.assertNoLocalRootRowMutation('Property type conversion');
  const meta = this.propertyMetaGet(toType);
  if (!meta) {
    return;
  }
  const currentType = this.propertyTypeGet(propertyId);
  if (!currentType) {
    return;
  }
  const currentData = this.propertyDataGet(propertyId);
  const rows = this.getLogicalRowIdsSnapshot();
  const currentCells = rows.map(rowId => this.cellValueGet(rowId, propertyId));
  const convertFunction = databasePropertyConverts.find(
    v => v.from === currentType && v.to === toType
  )?.convert;
  const result = convertFunction?.(
    currentData as any,

    currentCells as any
  ) ?? {
    property: meta.config.propertyData.default(),
    cells: currentCells.map(() => undefined),
  };
  this.captureStorageSync();
  updateProperty(this._model, propertyId, () => ({
    type: toType,
    data: result.property,
  }));
  const cells: Record<string, unknown> = {};
  currentCells.forEach((value, i) => {
    if (value != null || result.cells[i] != null) {
      const rowId = rows[i];
      if (rowId) {
        cells[rowId] = result.cells[i];
      }
    }
  });
  updateCells(this._model, propertyId, cells);
}

export function propertyTypeSetSharded(
  this: DatabaseSourceRuntime,
  propertyId: string,
  toType: string
): void {
  const meta = this.propertyMetaGet(toType);
  if (!meta) {
    return;
  }
  const currentType = this.propertyTypeGet(propertyId);
  if (!currentType) {
    return;
  }
  const currentData = this.propertyDataGet(propertyId);
  const rows = this.getLogicalRowIdsSnapshot();
  const currentCells = rows.map(rowId => this.cellValueGet(rowId, propertyId));
  const convertFunction = databasePropertyConverts.find(
    v => v.from === currentType && v.to === toType
  )?.convert;
  const result = convertFunction?.(currentData as any, currentCells as any) ?? {
    property: meta.config.propertyData.default(),
    cells: currentCells.map(() => undefined),
  };
  const serializedCells: Record<string, unknown> = {};
  currentCells.forEach((value, i) => {
    if (value != null || result.cells[i] != null) {
      const rowId = rows[i];
      if (rowId) {
        serializedCells[rowId] =
          this.serializeRawValueForProperty(
            toType,
            result.property,
            result.cells[i]
          ) ?? null;
      }
    }
  });

  this.captureStorageSync();
  updateProperty(this._model, propertyId, () => ({
    type: toType,
    data: result.property,
  }));
  this.writeShardedColumnCellsToLocalDocs(propertyId, serializedCells);
}
